import { useEffect, useState } from "react";
import type { OrderProductType } from "@/api/order";
import {
  applyInvoiceRecord,
  mapProductTypeToInvoiceOrderType,
  type InvoiceApplyData,
} from "@/api/invoice";
import { toastError } from "@/utils/toast";

const PHONE_RE = /^1\d{10}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatYuan(n: number) {
  const x = Number.isFinite(n) ? n : 0;
  return x.toLocaleString("zh-CN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function FieldLabel({
  children,
  required,
  optional,
}: {
  children: React.ReactNode;
  required?: boolean;
  optional?: boolean;
}) {
  return (
    <div className="text-xs text-gray-800 mb-1.5 flex items-center gap-0.5 flex-wrap leading-snug">
      <span>{children}</span>
      {required ? <span style={{ color: "#EF4444" }}>*</span> : null}
      {optional ? (
        <span className="text-gray-400 font-normal">（选填）</span>
      ) : null}
    </div>
  );
}

function typePillStyle(active: boolean): React.CSSProperties {
  if (active) {
    return {
      background: "#FFFBEB",
      border: "1.5px solid #FED7AA",
      color: "#B45309",
    };
  }
  return {
    background: "#F4F5F9",
    border: "1.5px solid #E5E7EB",
    color: "#6B7280",
  };
}

export interface InvoiceModalProps {
  orderId: number;
  productType: OrderProductType;
  /** 开票产品主标题 */
  productTitle: string;
  /** 副标题/规格说明（灰色小字一行） */
  productSubtitle?: string;
  /** 合计开票金额 */
  invoiceAmount: number;
  onClose: () => void;
  onSubmitSuccess?: () => void;
}

export default function InvoiceModal({
  orderId,
  productType,
  productTitle,
  productSubtitle = "",
  invoiceAmount,
  onClose,
  onSubmitSuccess,
}: InvoiceModalProps) {
  /** 1 电子普通发票 2 增值税专用发票 */
  const [vatKind, setVatKind] = useState<1 | 2>(1);
  const [entityKind, setEntityKind] = useState<"personal" | "company">(
    "personal",
  );
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [applyResult, setApplyResult] = useState<InvoiceApplyData | null>(null);
  const [formData, setFormData] = useState({
    title: "",
    taxId: "",
    email: "",
    phone: "",
    bank: "",
    bankNo: "",
    address: "",
  });

  useEffect(() => {
    setSubmitted(false);
    setSubmitting(false);
    setApplyResult(null);
    setVatKind(1);
    setEntityKind("personal");
    setFormData({
      title: "",
      taxId: "",
      email: "",
      phone: "",
      bank: "",
      bankNo: "",
      address: "",
    });
  }, [orderId]);

  const isCompany = entityKind === "company";
  const phoneOk =
    !formData.phone.trim() || PHONE_RE.test(formData.phone.trim());
  const taxOk =
    !isCompany ||
    (formData.taxId.trim().length >= 15 && formData.taxId.trim().length <= 22);
  const canSubmit =
    formData.title.trim() &&
    formData.email.trim() &&
    EMAIL_RE.test(formData.email.trim()) &&
    phoneOk &&
    taxOk;

  const handleSubmit = async () => {
    if (!Number.isFinite(orderId) || orderId <= 0) {
      toastError("订单信息异常，请返回重试");
      return;
    }
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const data = await applyInvoiceRecord({
        order_id: orderId,
        order_type: mapProductTypeToInvoiceOrderType(productType),
        head_name: formData.title.trim(),
        type: isCompany ? 2 : 1,
        invoice_type: vatKind,
        user_email: formData.email.trim(),
        duty_number: isCompany ? formData.taxId.trim() : "",
        phone: formData.phone.trim(),
        bank: isCompany ? formData.bank.trim() : "",
        bank_no: isCompany ? formData.bankNo.trim() : "",
        address: isCompany ? formData.address.trim() : "",
      });
      setApplyResult(data);
      setSubmitted(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : "提交失败，请稍后重试";
      toastError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleKnow = () => {
    onSubmitSuccess?.();
    onClose();
  };

  const amountText = formatYuan(invoiceAmount);
  const hasSub = Boolean(productSubtitle.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      style={{ background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="bg-white rounded-t-3xl w-full flex flex-col"
        style={{ maxWidth: 480, maxHeight: "min(92vh, 720px)" }}
      >
        <div className="w-10 h-1 rounded-full bg-gray-200 mx-auto mt-3 flex-shrink-0" />
        <div
          className="flex items-center justify-between px-5 pt-4 pb-3 flex-shrink-0"
          style={{ borderBottom: "1px solid #F5F5F5" }}
        >
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: "#F4F5F9" }}
          >
            <i
              className="ri-close-line"
              style={{ color: "#374151", fontSize: 18 }}
            />
          </button>
          <span className="text-base font-bold text-gray-900">申请开票</span>
          <div className="w-8" />
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-6">
          {submitted ? (
            <div className="flex flex-col items-center py-8">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-3"
                style={{ background: "#FFF7ED" }}
              >
                <i
                  className="ri-bill-line"
                  style={{ color: "#D97706", fontSize: 32 }}
                />
              </div>
              <div className="text-base font-bold text-gray-900 mb-1">
                开票申请已提交
              </div>
              {applyResult?.record_no ? (
                <div className="text-xs text-gray-500 text-center mb-1">
                  申请单号：{applyResult.record_no}
                </div>
              ) : null}
              <div className="text-xs text-gray-500 text-center px-2">
                处理结果将发送至邮箱：{formData.email}
              </div>
              <button
                type="button"
                onClick={handleKnow}
                className="mt-6 px-8 py-2.5 rounded-full text-sm font-semibold text-white cursor-pointer whitespace-nowrap"
                style={{ background: "#D97706" }}
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              {/* 开票产品 */}
              <div
                className="rounded-2xl p-3.5 mb-5"
                style={{
                  background: "#FFFBEB",
                  border: "1px solid #FDE68A",
                }}
              >
                <div className="flex items-center gap-1.5 mb-2">
                  <i
                    className="ri-shopping-bag-3-fill"
                    style={{ color: "#D97706", fontSize: 16 }}
                  />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: "#92400E" }}
                  >
                    开票产品
                  </span>
                </div>
                <div className="flex gap-2 items-start">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900 leading-snug line-clamp-2">
                      {productTitle || "—"}
                    </div>
                    {hasSub ? (
                      <div className="text-xs text-gray-500 mt-1 leading-relaxed line-clamp-3">
                        {productSubtitle.trim()}
                      </div>
                    ) : null}
                  </div>
                  <div
                    className="text-sm font-bold flex-shrink-0"
                    style={{ color: "#EF4444" }}
                  >
                    ¥{amountText}
                  </div>
                </div>
                <div
                  className="my-3"
                  style={{
                    borderTop: "1px dashed #FCD34D",
                  }}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-600">合计开票金额</span>
                  <span
                    className="text-lg font-bold"
                    style={{ color: "#EF4444" }}
                  >
                    ¥{amountText}
                  </span>
                </div>
              </div>

              {/* 发票类型 2×2 */}
              <div className="text-xs font-semibold text-gray-800 mb-2">
                发票类型
              </div>
              <div className="grid grid-cols-2 gap-2 mb-5">
                <button
                  type="button"
                  onClick={() => setVatKind(1)}
                  className="py-2.5 px-2 rounded-xl text-xs font-medium cursor-pointer text-center leading-snug"
                  style={typePillStyle(vatKind === 1)}
                >
                  电子普通发票
                </button>
                <button
                  type="button"
                  onClick={() => setVatKind(2)}
                  className="py-2.5 px-2 rounded-xl text-xs font-medium cursor-pointer text-center leading-snug"
                  style={typePillStyle(vatKind === 2)}
                >
                  增值税专用发票
                </button>
                <button
                  type="button"
                  onClick={() => setEntityKind("personal")}
                  className="py-2.5 px-2 rounded-xl text-xs font-medium cursor-pointer text-center"
                  style={typePillStyle(entityKind === "personal")}
                >
                  个人发票
                </button>
                <button
                  type="button"
                  onClick={() => setEntityKind("company")}
                  className="py-2.5 px-2 rounded-xl text-xs font-medium cursor-pointer text-center"
                  style={typePillStyle(entityKind === "company")}
                >
                  企业发票
                </button>
              </div>

              <div className="space-y-3.5 mb-6">
                <div>
                  <FieldLabel required>
                    {isCompany ? "企业名称" : "发票抬头"}
                  </FieldLabel>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#FAFAFA",
                      border: "1.5px solid #ECECEC",
                    }}
                    placeholder={
                      isCompany ? "请输入企业全称" : "请输入您的姓名"
                    }
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, title: e.target.value }))
                    }
                  />
                </div>

                {isCompany && (
                  <div>
                    <FieldLabel required>税务识别号</FieldLabel>
                    <input
                      className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                      style={{
                        background: "#FAFAFA",
                        border: "1.5px solid #ECECEC",
                      }}
                      placeholder="18位统一社会信用代码"
                      value={formData.taxId}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, taxId: e.target.value }))
                      }
                    />
                  </div>
                )}

                <div>
                  <FieldLabel required>接收邮箱</FieldLabel>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#FAFAFA",
                      border: "1.5px solid #ECECEC",
                    }}
                    placeholder="发票将发送至此邮箱"
                    inputMode="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                </div>

                <div>
                  <FieldLabel optional>手机号</FieldLabel>
                  <input
                    className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                    style={{
                      background: "#FAFAFA",
                      border: "1.5px solid #ECECEC",
                    }}
                    placeholder="用于发票通知"
                    inputMode="numeric"
                    maxLength={11}
                    autoComplete="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((p) => ({
                        ...p,
                        phone: e.target.value.replace(/\D/g, "").slice(0, 11),
                      }))
                    }
                  />
                </div>

                {isCompany && (
                  <>
                    <div>
                      <FieldLabel optional>注册地址</FieldLabel>
                      <input
                        className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                        style={{
                          background: "#FAFAFA",
                          border: "1.5px solid #ECECEC",
                        }}
                        placeholder="企业注册地址"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            address: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div>
                      <FieldLabel optional>开户行</FieldLabel>
                      <input
                        className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                        style={{
                          background: "#FAFAFA",
                          border: "1.5px solid #ECECEC",
                        }}
                        placeholder="开户行名称"
                        value={formData.bank}
                        onChange={(e) =>
                          setFormData((p) => ({ ...p, bank: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <FieldLabel optional>银行账号</FieldLabel>
                      <input
                        className="w-full rounded-xl px-3 py-2.5 text-sm outline-none"
                        style={{
                          background: "#FAFAFA",
                          border: "1.5px solid #ECECEC",
                        }}
                        placeholder="银行账号"
                        inputMode="numeric"
                        autoComplete="off"
                        value={formData.bankNo}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            bankNo: e.target.value.replace(/\s/g, ""),
                          }))
                        }
                      />
                    </div>
                  </>
                )}
              </div>

              <button
                type="button"
                disabled={!canSubmit || submitting}
                onClick={handleSubmit}
                className="w-full py-3 rounded-full text-sm font-semibold cursor-pointer whitespace-nowrap disabled:cursor-not-allowed"
                style={{
                  background: canSubmit && !submitting ? "#D97706" : "#F0F0F0",
                  color: canSubmit && !submitting ? "#fff" : "#9CA3AF",
                }}
              >
                {submitting ? "提交中…" : "提交开票申请"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
